import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { isProblemError } from '../../api/client';
import { useGetMe } from '../../api/generated/identity/identity';
import {
  useCreateMakeupRequest,
  useListOccurrences,
} from '../../api/generated/scheduling/scheduling';
import type { ClassOccurrence } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { formatInPersonTimezone } from '../../i18n/datetime';
import { SettledListView, settledList } from '../schedule/settledList';
import { instantFromWallClock } from '../schedule/wallClock';
import { useAllMakeupRequests } from './makeupRequests';
import styles from './makeup.module.css';

/**
 * Aulas que geram direito a reposicao: as que **nao vao acontecer**.
 *
 * Inclui a desmarcada com antecedencia, e nao so a ja perdida: o horario
 * liberado de terca que vem e exatamente o caso que a reposicao existe para
 * resolver, e exigir que a aula ja tivesse passado obrigaria o aluno a esperar
 * o prejuizo antes de poder pedir.
 */
const NOT_HAPPENING = new Set(['no_show', 'cancelled_by_student', 'cancelled_by_teacher']);

export function MakeupPage() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;
  // A fila inteira, e nao a primeira pagina dela: o LIMIT do servidor recai
  // sobre a lista mais recente, e o pedido que cai fora dela some da tela sem
  // aviso — inclusive o proprio, para o aluno que voltou depois de duas semanas.
  const listQuery = useAllMakeupRequests();
  const items = listQuery.items ?? [];

  return (
    <div className={styles.page}>
      <h1>{t('makeup.title')}</h1>
      <p>{t('makeup.intro')}</p>

      {/* Antes disto a tela **so listava**: o aluno nao tinha como pedir. */}
      {me?.role === 'student' ? (
        <MakeupRequestForm
          timezone={me.timezone}
          locale={me.locale}
          onRequested={() => listQuery.refetch()}
        />
      ) : null}

      {listQuery.isError ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
      {/* "Nenhum pedido" so vale quando o cursor acabou; antes disso a lista
          esta incompleta e o vazio seria uma afirmacao falsa. */}
      {items.length === 0 && !listQuery.isPending && !listQuery.isError ? (
        <p>{t('makeup.empty')}</p>
      ) : null}
      <ul className={styles.list}>
        {items.map((req) => (
          <li key={req.id}>
            {req.student.displayName} · {t(`makeup.state.${req.state}`)}
            {/*
              Responder e do PROFESSOR: `acceptMakeupRequest` e
              `declineMakeupRequest` sao `RoleTeacher` no contrato e `/makeup/:id
              /respond` esta fechada a esse papel em `routes.tsx`. O aluno via o
              link do proprio pedido e, ao clicar, era devolvido a home sem uma
              palavra — nao vazava nada, mas ensinava que a interface oferece
              acoes que nao existem para quem as ve.
            */}
            {req.state === 'requested' && me?.role === 'teacher' ? (
              <Link to={`/makeup/${req.id}/respond`}>{t('makeup.respondTitle')}</Link>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Janela de busca das aulas que nao vao acontecer.
 *
 * Vai do passado recente ao proximo mes: para tras porque a aula perdida ja
 * aconteceu, e para a frente porque a desmarcada com antecedencia ainda esta
 * agendada. Nao e o historico todo — reposicao de um ano atras nao existe.
 */
function makeupWindow(): { from: string; to: string } {
  const now = Date.now();
  return {
    from: new Date(now - 45 * 86400000).toISOString(),
    to: new Date(now + 30 * 86400000).toISOString(),
  };
}

/**
 * Pedido de reposicao.
 *
 * Duas recusas do contrato viram forma nesta tela, e nenhuma delas e cortesia:
 *
 * - **A aula de origem vem de uma lista, e nao de um campo de texto.** Reposicao
 *   se pede sobre uma ocorrencia perdida; digitar identificador a mao e a forma
 *   mais confiavel de errar um e receber 404.
 * - **O horario pedido responde 409 se violar as 24 h de antecedencia ou a
 *   janela da semana corrente.** A decisao e do servidor e a tela nao a imita:
 *   ela diz o motivo que voltou, em vez de mostrar "erro inesperado" e deixar o
 *   aluno tentando o mesmo horario de novo.
 */
function MakeupRequestForm({
  timezone,
  locale,
  onRequested,
}: {
  readonly timezone: string;
  readonly locale: string;
  readonly onRequested: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateMakeupRequest();
  // A janela nasce **uma vez por montagem**. Calcula-la no corpo do componente
  // produziria um `from`/`to` novo a cada render, e com eles uma chave de
  // consulta nova: a tela reconsultaria para sempre sem nunca assentar.
  const [window] = useState(makeupWindow);
  const occurrencesQuery = useListOccurrences(window);

  const [occurrenceId, setOccurrenceId] = useState('');
  const [requestedAt, setRequestedAt] = useState('');

  // "Nenhuma aula desmarcada no periodo" e uma afirmacao sobre o SERVIDOR, e nao
  // sobre a janela: dita enquanto a leitura corre e um palpite, e dita no lugar
  // de um 500 ela troca a falha por uma boa noticia. O aluno le que nao tem aula
  // perdida a repor, fecha a tela e perde a janela — `ExpireMakeupWindows` a
  // fecha por decurso de prazo, e ninguem volta para conferir uma frase
  // tranquilizadora. Mesmo derivador das filas de mudanca de horario.
  const missed = settledList<ClassOccurrence>({
    isError: occurrencesQuery.isError,
    // Consulta de pagina unica: nao ha cursor por seguir.
    hasNextPage: false,
    items:
      occurrencesQuery.data === undefined
        ? undefined
        : occurrencesQuery.data.items.filter((occ) => NOT_HAPPENING.has(occ.status)),
  });
  const canSubmit = occurrenceId.length > 0 && requestedAt.length > 0 && !create.isPending;
  const outOfWindow = isProblemError(create.error) && create.error.status === 409;
  const unexpected = create.error !== null && !outOfWindow;

  return (
    <section aria-label={t('makeup.request')}>
      <h2>{t('makeup.request')}</h2>

      <SettledListView state={missed} empty={t('makeup.noneMissed')}>
        {(occurrences) => (
          <form
            className={styles.form}
            onSubmit={(event) => {
              event.preventDefault();
              if (!canSubmit) return;
              create.mutate(
                {
                  occurrenceId,
                  // No fuso DA PESSOA, e nao no do navegador. `new Date('...T19:00')`
                  // resolve a hora civil pelo relogio do sistema operacional: o
                  // aluno de America/Sao_Paulo em viagem por Lisboa pedia 19h e o
                  // servidor recebia outro instante — podendo ainda violar as 24 h
                  // de antecedencia e voltar 409 por um horario que a tela mesma
                  // mostrava como valido.
                  data: { requestedStartsAtUtc: instantFromWallClock(requestedAt, timezone) },
                },
                {
                  onSuccess: () => {
                    setRequestedAt('');
                    onRequested();
                  },
                },
              );
            }}
          >
            {create.isSuccess ? <Alert tone="success">{t('makeup.sent')}</Alert> : null}
            {outOfWindow ? (
              <Alert tone="warning" title={t('makeup.outOfWindowTitle')}>
                {t('makeup.outOfWindowDetail')}
              </Alert>
            ) : null}
            {unexpected ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}

            <label className={styles.selectLabel}>
              {t('makeup.origin')}
              <select value={occurrenceId} onChange={(event) => setOccurrenceId(event.target.value)}>
                <option value="">{t('makeup.originPlaceholder')}</option>
                {occurrences.map((occ) => (
                  <option key={occ.id} value={occ.id}>
                    {formatInPersonTimezone(occ.startsAtUtc, timezone, locale)}
                  </option>
                ))}
              </select>
            </label>

            <Field
              label={t('makeup.requestedAt')}
              type="datetime-local"
              required
              hint={t('makeup.requestedAtHint')}
              value={requestedAt}
              onChange={(event) => setRequestedAt(event.target.value)}
            />

            <div>
              <Button variant="primary" type="submit" disabled={!canSubmit} loading={create.isPending}>
                {t('makeup.request')}
              </Button>
            </div>
          </form>
        )}
      </SettledListView>
    </section>
  );
}
